export type AuraRichTextToken =
    | { type: 'text'; content: string }
    | { type: 'strong'; content: string }
    | { type: 'link'; content: string; href: string };

export interface AuraRichTextLine {
    isBullet: boolean;
    tokens: AuraRichTextToken[];
}

const INLINE_MARKDOWN_PATTERN = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/gu;
const BULLET_PATTERN = /^\s*\*\s+/u;

const decodeEscapedWhitespace = (value: string): string =>
    value
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
        .replace(/\\t/g, '\t');

const hasControlCharacter = (value: string): boolean =>
    Array.from(value).some((character) => {
        const codePoint = character.codePointAt(0) || 0;
        return codePoint <= 0x1f || codePoint === 0x7f;
    });

const getSafeLink = (value: string): string | null => {
    const href = value.trim();
    const isSafeInternalPath =
        href.startsWith('/') &&
        !href.startsWith('//') &&
        !href.includes('\\') &&
        !hasControlCharacter(href);

    if (isSafeInternalPath) return href;

    try {
        const url = new URL(href);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

export const normalizeAuraRichText = (text: string): string => {
    if (!text) return '';

    const withoutFence = text
        .trim()
        .replace(/^```(?:json|javascript|js)?\s*/iu, '')
        .replace(/\s*```$/u, '')
        .trim();

    try {
        const parsed: unknown = JSON.parse(withoutFence);
        if (typeof parsed === 'object' && parsed !== null) {
            const record = parsed as Record<string, unknown>;
            if (typeof record.message === 'string' && record.message.trim()) {
                return decodeEscapedWhitespace(record.message.trim());
            }

            const summary = record.summary;
            if (typeof summary === 'object' && summary !== null) {
                const content = (summary as Record<string, unknown>).content;
                if (typeof content === 'string' && content.trim()) {
                    return decodeEscapedWhitespace(content.trim());
                }
            }
        }
    } catch {
        // Regular AURA responses are plain text or Markdown.
    }

    return decodeEscapedWhitespace(withoutFence);
};

const parseInlineTokens = (line: string): AuraRichTextToken[] => {
    const tokens: AuraRichTextToken[] = [];
    let currentIndex = 0;

    for (const match of line.matchAll(INLINE_MARKDOWN_PATTERN)) {
        const matchIndex = match.index ?? 0;
        if (matchIndex > currentIndex) {
            tokens.push({ type: 'text', content: line.slice(currentIndex, matchIndex) });
        }

        if (match[1]) {
            tokens.push({ type: 'strong', content: match[1] });
        } else {
            const label = match[2] || '';
            const href = getSafeLink(match[3] || '');
            tokens.push(
                href ? { type: 'link', content: label, href } : { type: 'text', content: match[0] },
            );
        }

        currentIndex = matchIndex + match[0].length;
    }

    if (currentIndex < line.length || tokens.length === 0) {
        tokens.push({ type: 'text', content: line.slice(currentIndex) });
    }

    return tokens;
};

export const parseAuraRichText = (text: string): AuraRichTextLine[] =>
    normalizeAuraRichText(text)
        .split('\n')
        .map((line) => {
            const isBullet = BULLET_PATTERN.test(line);
            const content = isBullet ? line.replace(BULLET_PATTERN, '') : line;

            return { isBullet, tokens: parseInlineTokens(content) };
        });
